import React, { ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { User } from "oidc-client-ts";
import { Menu } from "@imminently/react/ui/menu/menu";
import Logout from "@mui/icons-material/Logout";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";

import { Avatar, AvatarProps } from "@/components/Avatar";
import { css, Theme } from "@/theme";

export type AccountMenuProps = {
  avatarProps?: AvatarProps;

  /** The menu items to render */
  children?: ReactNode;
};

const styles = {
  av: (theme: Theme) => css`
    color: ${theme.palette.primary[500]};
    border-width: 0px;
    > span {
      font-size: 1.25rem;
      line-height: 1.2;
      font-weight: 700;
      font-family: ${theme.typography.h5.fontFamily};
    }
  `,
};

const sxStyles = {
  logout: { gap: "0.5rem" },
  button: {
    p: 0,
    background: "white",

    "&:hover": {
      background: "#cdd1e4",
    },
  },
};

const getUsername = (user: User | null | undefined) => {
  if (!user) return "?";
  if (user.profile?.given_name && user.profile?.family_name)
    return `${user.profile?.given_name} ${user.profile?.family_name}`;
  return user.profile?.name ?? "?";
};

export const AccountMenu = ({ children }: AccountMenuProps) => {
  const auth = useAuth();
  // In the hospital the name from AD is flipped and comes as 'Smith, Bob'. This code checks for this and flips back (otherwise the avatar is back to front)
  let name = getUsername(auth.user);
  if (name && name.includes(", ")) {
    const parts = name.split(", ");
    name = parts[1] + " " + parts[0];
  }
  const signout = () => auth.signoutRedirect();

  // Image="https://images.unsplash.com/photo-1492633423870-43d1cd2775eb?&w=128&h=128&dpr=2&q=80"
  const avatar = <Avatar css={styles.av} outlined name={name} alt={name} />;

  return (
    <Menu icon={avatar} buttonProps={{ sx: sxStyles.button }}>
      <MenuList>
        {children}
        <Divider />
        <MenuItem sx={sxStyles.logout} onClick={signout}>
          <Logout />
          Logout
        </MenuItem>
      </MenuList>
    </Menu>
  );
};
